import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import {
  ReactComponent as PrivateEye,
  ReactComponent as EyePrivateSvg
} from '@assets/icons/eye-private.svg';
import { Dropdown, Menu, Button, message } from 'antd';
import LottieComponent from '@components/Lottie';
import PageSortPanel from '@components/PageSortPanel';
import { useCurrentWorkspace } from '@hooks/workspace';
import { assetGetAssetsDownloadList } from '@api/Asset';
import './FolderItem.less';
import FolderEmptyState from '@pages/Campaigns/FolderEmptyState';
import { AssetsList } from '@components/Assets';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import {
  toggleApprovalConflictAssetsModal,
  toggleChangeFolderAccessModal,
  toggleEntityDeletionModal,
  toggleManageFolderAccessModal,
  toggleRenameEntityModal,
  toggleShareAssetModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import {
  AssetControllerListAssetsParams,
  AssetItemDto,
  CampaignItemDto
} from '@api/Api';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { useLayout } from '@layouts/PageLayout';

import { useOrganization } from '@components/OrganizationBoundary';
import { downloadAll } from '@helpers/download';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  useAssetsQuery,
  useAssetsCountQuery,
  useAssetQuery
} from '@hooks/queries/assets';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounced } from '@helpers/useDebounced';
import { isAxiosError } from 'axios';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';

import { ReactComponent as ApprovalUsersIconPanelSvg } from '@assets/icons/approval-users-icon-panel.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as EyePublicSvg } from '@assets/icons/eye-public.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as LinkPlusSvg } from '@assets/icons/dropdown/link-plus.svg';
import { showTranscriptionReadyToast } from '@helpers/showTranscriptionReadyToast';

type AssetsOrderBy = NonNullable<
  AssetControllerListAssetsParams['orderBy']
>[number];

function FolderItemInWorkspace({
  folder,
  campaign
}: {
  folder: AssetItemDto;
  campaign: CampaignItemDto;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentView } = useLayout();
  const { currentOrganization } = useOrganization();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);
  const [orderBy, setOrderBy] = useState<AssetsOrderBy>('order:DESC');

  const [searchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const queryClient = useQueryClient();
  const prevFolderId = useRef(folder?.id);

  const assetsQuery = useAssetsQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      orderBy: [orderBy]
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id
    }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: folder?.workspaceId as any,
      campaignId: folder?.campaignId as any,
      parentId: folder?.id as any,
      isFolder: false,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: !!folder,
      keepPreviousData: prevFolderId.current === folder?.id,
      placeholderData: folder?.folder?.assetsCount
    }
  );

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);

  useWebSocketSubscription((msg) => {
    if (!folder) return;
    if (msg.causedByMe) return;
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  }, 'folder-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!folder || msg.payload.folderId !== folder.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (!folder || msg.payload.folderId !== folder.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'folder-access-changed'
  );

  useWebSocketSubscription((msg) => {
    if (!campaign || msg.payload.campaignId !== campaign.id) return;
    if (msg.causedByMe) return;
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, 'campaign-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (!campaign || msg.payload.campaignId !== campaign.id) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-access-changed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.folderId,
    () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.campaignId,
    (messages) => {
      const msg = messages[messages.length - 1];
      const payload = msg?.payload;
      if (payload?.reason === 'transcription-completed') {
        showTranscriptionReadyToast({
          payload,
          onViewTranscript: () => {
            navigate(
              `/media-viewer/${payload.assetId}/${payload.assetVersionId}`
            );
          }
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    1000,
    (msg) => {
      if (msg.payload.folderId !== folder?.id) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  const upgradePrivateFolders =
    !currentOrganization.entity?.features.privateFolders;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization?.entity?.features.approvalRouting;

  const menuItems: ItemType[] = [
    {
      key: 'download-files',
      disabled: folder?.folder?.assetsCount === 0,
      label: 'Download files',
      icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
      onClick: async () => {
        if (!folder?.folder) return;
        const {
          data: { list }
        } = await assetGetAssetsDownloadList({
          campaignId: folder.campaignId,
          workspaceId: folder.workspaceId,
          parentId: folder.id
        });
        downloadAll(
          list.map((x) => x.sourceUrl),
          `${folder.folder.name}.zip`
        );
      }
    },
    {
      key: 'new-share-link',
      label: 'New share link',
      disabled:
        !campaign?.permissions.createReviews || !folder?.folder?.assetsCount,
      icon: <LinkPlusSvg />,
      onClick: () => {
        if (!campaign?.permissions.createReviews) return;
        dispatch(
          toggleShareAssetModal({
            visible: true,
            campaignId: folder.campaignId,
            folders: [folder.id],
            defaultName: folder.folder?.name || ''
          })
        );
      }
    },
    {
      type: 'divider'
    },
    ...(folder?.folder?.permissions.rename
      ? [
          {
            key: 'rename-folder',
            label: 'Rename',
            icon: <RenameSvg />,
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  entity: folder as AssetItemDto,
                  page: 'folder',
                  visible: true
                })
              );
            }
          },
          {
            type: 'divider' as const
          }
        ]
      : []),
    ...(folder?.folder?.permissions.setVisibility
      ? [
          {
            key: 'toggle-access',
            label: (
              <UpgradePlanTooltip
                text=" to a Team plan to access this feature."
                isCanVisible={!folder.isPrivate && upgradePrivateFolders}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                  }}
                >
                  {folder?.isPrivate ? 'Make public' : 'Make private'}

                  {!folder.isPrivate && upgradePrivateFolders && (
                    <UpgradePlanSvg className="upgrade-icon" />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            disabled: !folder.isPrivate && upgradePrivateFolders,
            icon: folder?.isPrivate ? (
              <EyePublicSvg className="b-opacity-icon" />
            ) : (
              <EyePrivateSvg className="b-opacity-icon" />
            ),
            onClick: () => {
              if (!folder.isPrivate && upgradePrivateFolders) return;
              if (!folder?.folder) return;
              dispatch(
                toggleChangeFolderAccessModal({
                  folder,
                  visible: true,
                  callback: async (folder: AssetItemDto) => {
                    if (!folder.isPrivate) return;
                    dispatch(
                      toggleManageFolderAccessModal({
                        folder,
                        visible: true
                      })
                    );
                  }
                })
              );
            }
          }
        ]
      : []),
    ...(folder?.folder?.permissions?.inviteContributors ||
    folder?.folder?.permissions?.inviteFromList
      ? [
          {
            key: 'manage-users',
            label: 'Manage users',
            icon: <ApprovalUsersIconPanelSvg />,
            onClick: () => {
              if (!folder) return;
              dispatch(
                toggleManageFolderAccessModal({ folder, visible: true })
              );
            }
          }
        ]
      : []),
    ...(folder?.folder?.permissions?.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            disabled: !folder?.folder?.permissions.delete,
            icon: <DeleteSvg className="b-opacity-icon" />,
            onClick: () => {
              if (!folder?.folder?.permissions.delete) return;
              dispatch(
                toggleEntityDeletionModal({
                  entity: folder as AssetItemDto,
                  page: 'folder',
                  permanent: false,
                  visible: true,
                  func: () => navigate(`../`)
                })
              );
            }
          }
        ]
      : [])
  ];

  return (
    <div>
      <div className="header-container">
        <div className="title-container">
          <span className="folder-name">
            {folder?.isPrivate && (
              <div>
                <PrivateEye />
              </div>
            )}
            <div>{folder?.folder?.name}</div>
          </span>
          <Dropdown
            menu={{
              items: menuItems,
              onClick: (e) => {
                e.domEvent.stopPropagation();
              }
            }}
            overlayClassName="asset_links_overlay"
            placement="bottomLeft"
            trigger={['click']}
          >
            <Button
              className="header-container__dropdown-trigger"
              type="text"
              size="small"
            >
              <ActionSvg style={{ marginRight: 0 }} />
            </Button>
          </Dropdown>
        </div>
        <UpgradePlanTooltip
          text={
            !canUseBatchApprovals || !awFeatureEnabled
              ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
              : 'Click to route all media in folder for approval'
          }
          isCanVisible={true}
          placement="topRight"
          isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
          innerStyle={{
            maxWidth: '300px'
          }}
        >
          <>
            <Button
              type="default"
              size="small"
              className="info-button"
              disabled={!canUseBatchApprovals || !awFeatureEnabled}
              onClick={() => {
                if (!canUseBatchApprovals || !awFeatureEnabled) return;
                dispatch(
                  toggleApprovalConflictAssetsModal({
                    visible: true,
                    title: 'Route folder for approval',
                    campaignId: folder?.campaignId,
                    workspaceId: folder?.workspaceId,
                    parentId: folder?.id,
                    callback: (aw) => {
                      if (aw) {
                        navigate(
                          `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                        );
                        return;
                      }
                      queryClient.invalidateQueries({ queryKey: ['assets'] });
                    }
                  })
                );
              }}
            >
              <span
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}
              >
                <ApprovalRoutingSvg className="info-button-icon" />
                <span>Route folder for approval</span>
                {(!canUseBatchApprovals || !awFeatureEnabled) && (
                  <UpgradePlanSvg className="upgrade-icon" />
                )}
              </span>
            </Button>
          </>
        </UpgradePlanTooltip>
      </div>
      <PageSortPanel
        entity="in folder"
        onAddClick={
          campaign?.demo
            ? undefined
            : () => {
                if (!campaign) return;
                dispatch(
                  toggleUploadAssetModal({
                    visible: true,
                    workspaceId: campaign.workspaceId,
                    campaignId: campaign.id,
                    folderId: folder.id
                  })
                );
              }
        }
        actionButtonName="Upload new media"
        onSearch={setSearchQuery}
        onOrderBy={undefined}
        classNameSearchContainer={undefined}
        classNameActionButton={undefined}
        sortMenuModal={
          <Menu
            selectedKeys={[orderBy]}
            onClick={async ({ key }) => setOrderBy(key as AssetsOrderBy)}
          >
            <span className="help_dropdown__title">Sort by</span>
            <Menu.Item key="order:DESC">
              <span className="help_dropdown__dot" /> Custom
            </Menu.Item>
            <Menu.Item key="createdAt:DESC">
              <span className="help_dropdown__dot" /> Date created
            </Menu.Item>
            <Menu.Item key="modifiedAt:DESC">
              <span className="help_dropdown__dot" /> Date modified
            </Menu.Item>
          </Menu>
        }
        searchInputComponent={undefined}
        classNamePanel={undefined}
      />
      <div
        className="menu-panel-container"
        style={{
          overflow: 'auto',
          marginTop: 24,
          paddingRight: 34,
          paddingLeft: 34,
          marginRight: -34,
          marginLeft: -34,
          height: 'calc(100vh - 275px - var(--demo-mode-panel-width, 0px))'
        }}
      >
        {assetsCount > 0 ? (
          <InfiniteScroll
            loading={assetsQuery.isFetchingNextPage ? 'more' : false}
            next={assetsQuery.fetchNextPage}
            hasMore={assetsQuery.hasNextPage ?? false}
          >
            <AssetsList
              campaign={campaign}
              folderId={folder.id}
              searchQuery={searchQuery}
              assetsList={assetsList}
              displayAssetsCount={assetsCount}
              isTileView={currentView === 'tile'}
              page="campaign"
              canCombine={true}
              countToFetch={assetsQuery.isFetchingNextPage ? assetsToFetch : 0}
              listLoading={assetsQuery.isLoading}
              canReorganize={orderBy === 'order:DESC'}
              isShowCompany={false}
              onRefreshAssetList={assetsQuery.refetch}
            />
          </InfiniteScroll>
        ) : (
          <FolderEmptyState campaignId={campaign.id} folderId={folder.id} />
        )}
      </div>
    </div>
  );
}

function FolderItem() {
  const { campaignId, folderId } = useParams();
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);

  const campaignQuery = useCampaignQuery(
    { campaignId: campaignId || '' },
    { enabled: !!campaignId }
  );
  const campaign = campaignQuery?.data;
  const campaignError = campaignQuery?.error;
  const folderQuery = useAssetQuery(
    {
      assetId: folderId || '',
      isFolder: true
    },
    { enabled: !!folderId }
  );
  const folderError = folderQuery?.error;
  const folder = folderQuery?.data;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAxiosError(campaignError)) return;
    if (!currentWorkspace?.id) return;
    const status = campaignError?.response?.status;
    if (status !== 403 && status !== 404) return;
    message.error('This project is no longer available.');
    navigate(`/campaigns/all/${currentWorkspace.id}`);
  }, [campaignError, currentWorkspace?.id, navigate]);

  useEffect(() => {
    if (!isAxiosError(folderError)) return;
    const status = folderError?.response?.status;
    if (status !== 403 && status !== 404) return;
    message.error('This folder is no longer available.');
    navigate('../');
  }, [folderError, navigate]);

  useEffect(() => {
    if (!campaign) return;
    if (campaign.workspaceId === currentWorkspace?.id) return;
    setCurrentWorkspace(campaign.workspaceId);
  }, [campaign, currentWorkspace, setCurrentWorkspace]);

  const fullPageLoader = (
    <div className="page_spinner_overlay">
      <LottieComponent className="page_spinner" view="preloader" />
    </div>
  );

  if (!folder || !campaign || !currentWorkspace) {
    return fullPageLoader;
  }
  if (campaign.workspaceId !== currentWorkspace.id) {
    return fullPageLoader;
  }
  if (folder.campaignId !== campaign.id) return null;
  return <FolderItemInWorkspace folder={folder} campaign={campaign} />;
}

export default FolderItem;
